import React, { useState, useEffect } from 'react';
import TextCon from '../textContent';
import './CookieConsent.scss';

const CookieConsent = ({ lang }) => {
  const [showBanner, setShowBanner] = useState(false); // 底部提示条
  const [showPopup, setShowPopup] = useState(false); // 偏好中心弹窗
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // 绝对必要 - 始终启用
    performance: true, // 性能 - 默认开启
    functional: true, // 功能 - 默认开启
    targeting: true, // 定向 - 默认开启
  });
  const [expandedSections, setExpandedSections] = useState({
    necessary: true,
    performance: false,
    functional: false,
    targeting: false,
  });

  useEffect(() => {
    // 检查是否已经有用户的选择
    const savedPreferences = sessionStorage.getItem('cookiePreferences');
    if (!savedPreferences) {
      // 首次访问，显示底部提示条
      setShowBanner(true);
    } else {
      try {
        const prefs = JSON.parse(savedPreferences);
        setCookiePreferences(prefs);
        applyCookieSettings(prefs);
      } catch (e) {
        console.error('Failed to parse cookie preferences:', e);
      }
    }
  }, []);

  // 应用Cookie设置
  const applyCookieSettings = (prefs) => {
    // 只有在用户同意时才注入非必要的Cookie
    if (prefs.performance) {
      enablePerformanceCookies();
    }
    if (prefs.functional) {
      enableFunctionalCookies();
    }
    if (prefs.targeting) {
      enableTargetingCookies();
    }
    
    // 保存同意状态
    document.cookie = "cookie_consent=accepted; path=/; Secure; SameSite=Strict";
  };

  // 启用性能Cookie
  const enablePerformanceCookies = () => {
    // 在这里添加性能相关的Cookie逻辑
    console.log('Performance cookies enabled');
  };

  // 启用功能Cookie
  const enableFunctionalCookies = () => {
    // 在这里添加功能相关的Cookie逻辑
    console.log('Functional cookies enabled');
  };

  // 启用定向Cookie
  const enableTargetingCookies = () => {
    // 在这里添加定向/广告相关的Cookie逻辑
    console.log('Targeting cookies enabled');
  };

  // 切换展开/收起
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // 切换开关
  const togglePreference = (type) => {
    if (type === 'necessary') return; // 绝对必要的Cookie不能关闭
    
    setCookiePreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // 关闭底部提示条
  const closeBanner = () => {
    setShowBanner(false);
  };

  // 打开偏好中心弹窗
  const openPreferenceCenter = () => {
    setShowPopup(true);
    setShowBanner(false);
  };

  // 全部拒绝
  const rejectAll = () => {
    const minimalPrefs = {
      necessary: true,
      performance: false,
      functional: false,
      targeting: false,
    };
    setCookiePreferences(minimalPrefs);
    saveAndApply(minimalPrefs);
  };

  // 确认选择
  const confirmSelection = () => {
    saveAndApply(cookiePreferences);
  };

  // 保存并应用设置
  const saveAndApply = (prefs) => {
    sessionStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    setShowPopup(false);
    setShowBanner(false);
    applyCookieSettings(prefs);
  };

  // 如果都不显示，返回null
  if (!showBanner && !showPopup) return null;

  return (
    <>
      {/* 底部提示条 */}
      {showBanner && (
        <div className="cookie-banner">
          <div className="cookie-banner-content">
            <p className="cookie-banner-text">
              {TextCon['cookieBannerText'][lang] || TextCon['cookieBannerText']['zh']}
              <a href="/privacy" className="cookie-banner-link">
                {TextCon['cookiePrivacyLink'][lang] || TextCon['cookiePrivacyLink']['zh']}
              </a>
            </p>
          </div>
          <div className="cookie-banner-actions">
            <button 
              className="cookie-banner-btn"
              onClick={openPreferenceCenter}
            >
              {TextCon['cookieSettingsBtn'][lang] || TextCon['cookieSettingsBtn']['zh']}
            </button>
            <button 
              className="cookie-banner-close"
              onClick={closeBanner}
              aria-label="关闭"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 偏好中心弹窗 */}
      {showPopup && (
        <div className="cookie-consent-overlay">
          <div className="cookie-consent-container">
            {/* 标题 */}
            <h2 className="cookie-consent-heading">
              {TextCon['cookieCenterTitle'][lang] || TextCon['cookieCenterTitle']['zh']}
            </h2>

            {/* 说明文字 */}
            <div className="cookie-consent-description">
              <p>{TextCon['cookieCenterDesc1'][lang] || TextCon['cookieCenterDesc1']['zh']}</p>
              <p>{TextCon['cookieCenterDesc2'][lang] || TextCon['cookieCenterDesc2']['zh']}</p>
            </div>

            {/* 管理偏好 */}
            <h3 className="cookie-manage-title">
              {TextCon['cookieManageTitle'][lang] || TextCon['cookieManageTitle']['zh']}
            </h3>

            {/* Cookie类别列表 */}
            <div className="cookie-categories">
              {/* 绝对必要的Cookie */}
              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleSection('necessary')}>
                  <span className="cookie-toggle-icon">{expandedSections.necessary ? '−' : '+'}</span>
                  <span className="cookie-category-name">
                    {TextCon['cookieNecessary'][lang] || TextCon['cookieNecessary']['zh']}
                  </span>
                  <span className="cookie-status always-active">
                    {TextCon['cookieAlwaysActive'][lang] || TextCon['cookieAlwaysActive']['zh']}
                  </span>
                </div>
                {expandedSections.necessary && (
                  <div className="cookie-category-content">
                    <p>{TextCon['cookieNecessaryDesc'][lang] || TextCon['cookieNecessaryDesc']['zh']}</p>
                  </div>
                )}
              </div>

              {/* 性能Cookie */}
              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleSection('performance')}>
                  <span className="cookie-toggle-icon">{expandedSections.performance ? '−' : '+'}</span>
                  <span className="cookie-category-name">
                    {TextCon['cookiePerformance'][lang] || TextCon['cookiePerformance']['zh']}
                  </span>
                  <label className="cookie-switch">
                    <input type="checkbox" checked={cookiePreferences.performance} onChange={() => togglePreference('performance')} />
                    <span className="slider"></span>
                  </label>
                </div>
                {expandedSections.performance && (
                  <div className="cookie-category-content">
                    <p>{TextCon['cookiePerformanceDesc'][lang] || TextCon['cookiePerformanceDesc']['zh']}</p>
                  </div>
                )}
              </div>

              {/* 功能Cookie */}
              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleSection('functional')}>
                  <span className="cookie-toggle-icon">{expandedSections.functional ? '−' : '+'}</span>
                  <span className="cookie-category-name">
                    {TextCon['cookieFunctional'][lang] || TextCon['cookieFunctional']['zh']}
                  </span>
                  <label className="cookie-switch">
                    <input type="checkbox" checked={cookiePreferences.functional} onChange={() => togglePreference('functional')} />
                    <span className="slider"></span>
                  </label>
                </div>
                {expandedSections.functional && (
                  <div className="cookie-category-content">
                    <p>{TextCon['cookieFunctionalDesc'][lang] || TextCon['cookieFunctionalDesc']['zh']}</p>
                  </div>
                )}
              </div>

              {/* 定向Cookie */}
              <div className="cookie-category">
                <div className="cookie-category-header" onClick={() => toggleSection('targeting')}>
                  <span className="cookie-toggle-icon">{expandedSections.targeting ? '−' : '+'}</span>
                  <span className="cookie-category-name">
                    {TextCon['cookieTargeting'][lang] || TextCon['cookieTargeting']['zh']}
                  </span>
                  <label className="cookie-switch">
                    <input type="checkbox" checked={cookiePreferences.targeting} onChange={() => togglePreference('targeting')} />
                    <span className="slider"></span>
                  </label>
                </div>
                {expandedSections.targeting && (
                  <div className="cookie-category-content">
                    <p>{TextCon['cookieTargetingDesc'][lang] || TextCon['cookieTargetingDesc']['zh']}</p>
                  </div>
                )}
              </div>
            </div>

            {/* 底部按钮 */}
            <div className="cookie-buttons">
              <button className="cookie-btn cookie-btn-reject" onClick={rejectAll}>
                {TextCon['cookieRejectAll'][lang] || TextCon['cookieRejectAll']['zh']}
              </button>
              <button className="cookie-btn cookie-btn-confirm" onClick={confirmSelection}>
                {TextCon['cookieConfirm'][lang] || TextCon['cookieConfirm']['zh']}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsent;
